import React, { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { helmetJsonLdProp } from 'react-schemaorg';
import { BreadcrumbList } from 'schema-dts';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  CategorySlug,
  SOCIAL_MEDIA_THUMBNAIL_URL,
  SUPPORTED_LANGUAGES,
} from '../../config';
import { useInitialState } from '../../contexts/InitialStateContext';
import { joinWithSlashes } from '../../utils/joinWithSlashes';
import { useSelectedCategory } from '../../hooks/useSelectedCategory';
import { useCurrentPage } from '../../hooks/useCurrentPage';
import { useInternalRoutes } from '../../hooks/useInternalRoutes';
import { useLocation } from 'react-router-dom';

export const HtmlHead: FC = () => {
  const { t } = useTranslation();
  const { baseUrl, locale } = useInitialState();
  const selectedCategory = useSelectedCategory();
  const page = useCurrentPage();
  const routes = useInternalRoutes();
  const rootUrl = new URL(baseUrl).origin;

  const isHomePage = selectedCategory?.slug === CategorySlug.DEFAULT;

  const canonicalLink = joinWithSlashes(baseUrl, useLocation().pathname);
  const canonicalLinkWithoutPage =
    isHomePage || !selectedCategory
      ? baseUrl
      : joinWithSlashes(baseUrl, routes.category.build(selectedCategory.slug));
  const defaultCanonicalLink = replaceLanguageInDomain(canonicalLink, 'en');
  const defaultCanonicalLinkWithoutPage = replaceLanguageInDomain(
    canonicalLinkWithoutPage,
    'en',
  );
  let title = t('seo.new.title.home', { page });
  let description = t('seo.new.description.home');

  if (
    !isHomePage &&
    selectedCategory &&
    selectedCategory.slug === CategorySlug.DESIGNED_TEMPLATES
  ) {
    title = t('seo.new.title.designed', { page });
    description = t('seo.new.description.designed');
  } else if (!isHomePage && selectedCategory) {
    const category = selectedCategory.displayName;
    title = t('seo.new.title.category', {
      category,
      page,
    });
    description = t('seo.new.description.category', { category });
  }

  const elements = [
    {
      name: t('breadcrumbs.wixcom'),
      item: rootUrl,
    },
    {
      name: t('breadcrumbs.templates'),
      item: `${rootUrl}/website/templates`,
    },
    {
      name: t('breadcrumbs.home'),
      item: `${rootUrl}/studio/templates`,
    },
  ];

  elements.splice(elements.length - 1, 1, {
    name: selectedCategory
      ? selectedCategory.displayName
      : t('breadcrumbs.home'),
    item: canonicalLink,
  });

  const breadcrumbsListScriptProps = helmetJsonLdProp<BreadcrumbList>({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: elements.map((params, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      ...params,
    })),
  });

  const favicon = [
    <link
      rel="icon"
      sizes="192x192"
      href="https://static.wixstatic.com/shapes/ee1397_18a51f58ce2447b083f693dff36b019f.svg"
      type="image/svg+xml"
      key="favicon-1"
    />,
    <link
      rel="shortcut icon"
      href="https://static.wixstatic.com/shapes/ee1397_18a51f58ce2447b083f693dff36b019f.svg"
      type="image/svg+xml"
      key="favicon-2"
    />,
    <link
      rel="apple-touch-icon"
      href="https://static.wixstatic.com/shapes/ee1397_18a51f58ce2447b083f693dff36b019f.svg"
      type="image/svg+xml"
      key="favicon-3"
    />,
  ];

  const isNoIndex = false;

  return (
    <Helmet script={[breadcrumbsListScriptProps]}>
      {isNoIndex && <meta name="robots" content="noindex" />}
      {favicon}
      <title>{title}</title>
      <meta name="description" content={description} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@wix" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={SOCIAL_MEDIA_THUMBNAIL_URL} />

      <meta name="og:type" content="website" />
      <meta name="og:title" content={title} />
      <meta name="og:description" content={description} />
      <meta name="og:url" content={canonicalLink} />
      <meta property="og:image" content={SOCIAL_MEDIA_THUMBNAIL_URL} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="628" />

      <link rel="canonical" href={canonicalLink} />
      <link
        rel="alternate"
        hrefLang="x-default"
        href={
          locale === 'en'
            ? defaultCanonicalLink
            : defaultCanonicalLinkWithoutPage
        }
      />
      {SUPPORTED_LANGUAGES.map((lang) => (
        <link
          rel="alternate"
          key={lang}
          hrefLang={lang}
          href={replaceLanguageInDomain(
            lang === locale ? canonicalLink : canonicalLinkWithoutPage,
            lang,
          )}
        />
      ))}
    </Helmet>
  );
};

const replaceLanguageInDomain = (url: string, lang: string) => {
  const newUrl = new URL(url);
  const langDomain = lang === 'en' ? 'www' : lang;
  newUrl.hostname = newUrl.hostname.replace(/^\w+\./, `${langDomain}.`);
  return newUrl.toString();
};
